import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import {
  QUARTERLY_OKR,
  QUARTERLY_OKR_BRIEF,
  QUARTERLY_OKR_LIVE_REVIEW,
  QUARTERLY_OKR_REVIEW,
} from "../../../../data/RouteUrls";
import { errorNoti } from "../../../../base/BasicNotifications";
import {
  formatMeetingDateTime,
  isMeetingPast,
} from "../../../../utils/meetingDateTime";
import { ArrowRightIcon } from "../../StrategyCanvas/StrategyCanvasHelpers";
import SummaryGroups, {
  FilterChips,
} from "../../StrategyCanvas/SummaryPanel/SummaryPanel";
import AnnualOkrReviewSkeleton from "../../AnnualOkr/AnnualOkrReview/AnnualOkrReviewSkeleton";
import {
  COMMENT_SCOPE,
  OBJECTIVE_TYPE,
  REVIEWABLE_STATES,
  REVIEW_SENT_STATES,
  REVIEW_STATUS,
  RUN_STATE,
  SEND_MODE,
  objectiveKey,
  readError,
} from "../_config/quarterlyOkrConfig";
import { briefRoute, routeForQuarter } from "../_config/quarterlyOkrRoutes";
import quarterlyOkrStore, {
  useQuarterlyOkrStore,
} from "../_store/quarterlyOkrStore";
import QokrBanner from "../_shared/QokrBanner/QokrBanner";
import QokrModeToggle from "../_shared/QokrModeToggle/QokrModeToggle";
import QokrAlignmentView, {
  alignmentFromReview,
} from "../_shared/QokrAlignmentView/QokrAlignmentView";
import ReviewCompanyOkrs from "./ReviewCompanyOkrs/ReviewCompanyOkrs";
import ReviewIndividualOkrs from "./ReviewIndividualOkrs/ReviewIndividualOkrs";
import ReviewCommentThread from "./ReviewCommentThread/ReviewCommentThread";
import ReviewCompleteModal from "./ReviewCompleteModal/ReviewCompleteModal";
import StartLiveReviewDialog from "./StartLiveReviewDialog/StartLiveReviewDialog";
import useReviewActions, {
  commentKey,
  commentsFor,
  deletingCommentId,
} from "./useReviewActions";
import { buildReviewSummary } from "./reviewSummary";
import "../_shared/QokrPrimitives/_qokr-pill.scss";
import "../../StrategyCanvas/StrategyCanvasReview/ReviewMode/StrategyCanvasReviewMode.scss";
import "../../AnnualOkr/AnnualOkrReview/AnnualOkrReview.scss";
import "../_shared/_qokr-tabs.scss";
import "./_quarterly-okr-review.scss";

// ═══════════════════════════════════════════════════════════════════════════
// `/coach-panel/quarterly-okrs/:quarterCompanyId/review` — the coach's review
// (§9.3–9.5). One path, three states by `run_state`:
//   • with the coach (SUBMITTED_FOR_COACH / COACH_REVIEW_IN_PROGRESS) — mark
//     every objective, comment, then complete & send to the CEO;
//   • sent (COACH_REVIEW_COMPLETED / LIVE_REVIEW_SCHEDULED) — read only, with
//     the booking and "Start live review";
//   • anything later goes where it belongs (live review, summary).
// The Annual OKR review's two-panel shell (`scrm-*`, `rm-*`, `rr-*`), with
// two tabs over the one `GET review` payload: Company OKRs and Individual
// OKRs (each person with their flags, comments and objectives).
// ═══════════════════════════════════════════════════════════════════════════

const selectReviews = (state) => state.reviewByQc;
const selectClients = (state) => state.clients;

const TAB = { COMPANY: "company", INDIVIDUAL: "individual" };

// Review Mode | Alignment Mode (2026-10-28) — Strategy Canvas's Review /
// Canvas Mode, for Quarterly OKRs. Alignment Mode is the read-only "how OKRs
// link up" view: each company objective with the team objectives linked to it.
const MODE = { REVIEW: "review", ALIGNMENT: "alignment" };
const MODE_OPTIONS = [
  { key: MODE.REVIEW, label: "Review Mode" },
  { key: MODE.ALIGNMENT, label: "Alignment Mode" },
];

const allObjectives = (review) => [
  ...(review?.company_objectives || []).map((o) => [OBJECTIVE_TYPE.COMPANY, o]),
  ...(review?.individual_objectives || []).map((o) => [
    OBJECTIVE_TYPE.INDIVIDUAL,
    o,
  ]),
];

const unmarkedKeys = (review) =>
  allObjectives(review)
    .filter(([, o]) => !o.review?.status)
    .map(([type, o]) => objectiveKey(type, o.objective_id));

const QuarterlyOkrReview = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const HeaderMetaData = useContext(HeaderContext);
  const { quarterCompanyId: qcId } = useParams();
  const { search } = useLocation();

  const review = useQuarterlyOkrStore(selectReviews)[qcId];
  const clients = useQuarterlyOkrStore(selectClients);
  const actions = useReviewActions(qcId);

  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState(TAB.COMPANY);
  const [mode, setMode] = useState(MODE.REVIEW);
  const [expandedKey, setExpandedKey] = useState(null);
  const [summaryFilter, setSummaryFilter] = useState("all");
  const [flaggedKeys, setFlaggedKeys] = useState(new Set());
  const [dialog, setDialog] = useState(null); // "complete" | "start"
  const leftRef = useRef(null);

  const companyName = review?.company?.name || "";
  const quarterRow = (clients || [])
    .flatMap((client) => client.quarters || [])
    .find((q) => String(q.quarter_company_id) === String(qcId));
  const quarterLabel = quarterRow?.label || "";
  // With a coach brief (handoff 34) the review sits under it, like the
  // Strategy Canvas: back and the company crumb go to the brief.
  const hasBrief = Boolean(quarterRow?.brief_status);
  const backTo = hasBrief ? briefRoute(qcId) : QUARTERLY_OKR;

  useEffect(() => {
    document.title = "Art of Scale | Quarterly OKR Review";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Quarterly OKRs" });
    // The dashboard row carries the quarter's label; fetched for a coach who
    // lands here from the email link.
    if (!quarterlyOkrStore.getState().clients) {
      quarterlyOkrStore
        .getState()
        .fetchDashboard()
        .catch(() => null);
    }
    // `enter` is a no-op past the first open, so calling it on every entry
    // is safe and keeps the dashboard button simple.
    const store = quarterlyOkrStore.getState();
    store
      .enterReview(qcId)
      .then(() => store.fetchReview(qcId, { force: true }))
      .then(() => setLoadError(null))
      .catch((error) => setLoadError(readError(error).message));
    // eslint-disable-next-line
  }, [qcId]);

  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(
        QUARTERLY_OKR_REVIEW,
        hasBrief
          ? [
              breadCrumbsArray[QUARTERLY_OKR][0],
              {
                title: companyName || "Brief",
                url: QUARTERLY_OKR_BRIEF.replace(":quarterCompanyId", qcId),
              },
              { title: "Review", url: "" },
            ]
          : [
              breadCrumbsArray[QUARTERLY_OKR][0],
              { title: companyName || "Quarterly OKR review", url: "" },
            ],
      ),
    );
  }, [dispatch, companyName, hasBrief, qcId]);

  // Past the review the run belongs on another screen.
  useEffect(() => {
    if (!review?.run_state) return;
    const owned =
      REVIEWABLE_STATES.includes(review.run_state) ||
      REVIEW_SENT_STATES.includes(review.run_state);
    if (owned) return;
    const route = routeForQuarter({
      quarter_company_id: qcId,
      run_state: review.run_state,
    });
    if (route) history.replace(route);
  }, [review?.run_state, qcId, history]);

  // First objective opens itself once, so the coach's first click isn't
  // spent revealing what they came to read. From the brief (handoff 34),
  // `?objective=<id>` opens and scrolls to that company objective instead,
  // and `?member=<user_id>` to that person on Individual OKRs.
  const openedInitial = useRef(false);
  useEffect(() => {
    if (openedInitial.current || !review) return;
    const params = new URLSearchParams(search);
    const objectiveId = params.get("objective");
    const memberId = params.get("member");
    const scrollTo = (selector) =>
      // Twice: once for the tab switch to paint, once for the row to open.
      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          leftRef.current
            ?.querySelector(selector)
            ?.scrollIntoView({ behavior: "smooth", block: "start" }),
        ),
      );
    if (
      objectiveId &&
      (review.company_objectives || []).some(
        (o) => String(o.objective_id) === objectiveId,
      )
    ) {
      openedInitial.current = true;
      setTab(TAB.COMPANY);
      setExpandedKey(objectiveKey(OBJECTIVE_TYPE.COMPANY, objectiveId));
      scrollTo(
        `[data-review-target="${OBJECTIVE_TYPE.COMPANY}-${objectiveId}"]`,
      );
      return;
    }
    if (
      memberId &&
      (review.members || []).some((m) => String(m.user_id) === memberId)
    ) {
      openedInitial.current = true;
      setTab(TAB.INDIVIDUAL);
      scrollTo(`[data-review-member="${memberId}"]`);
      return;
    }
    const first = allObjectives(review)[0];
    if (!first) return;
    openedInitial.current = true;
    setExpandedKey(objectiveKey(first[0], first[1].objective_id));
  }, [review, search]);

  const summaryGroups = useMemo(() => buildReviewSummary(review), [review]);
  const countWhere = (predicate) =>
    summaryGroups.reduce(
      (total, group) => total + group.items.filter(predicate).length,
      0,
    );
  const visibleGroups = useMemo(() => {
    const keep =
      summaryFilter === "discuss"
        ? (item) => item.status === REVIEW_STATUS.TO_DISCUSS
        : summaryFilter === "comments"
          ? (item) => item.comments.length > 0
          : null;
    if (!keep) return summaryGroups;
    return summaryGroups
      .map((group) => ({ ...group, items: group.items.filter(keep) }))
      .filter((group) => group.items.length);
  }, [summaryGroups, summaryFilter]);

  // The submit gate (§9.3), read with the server's own rule.
  const unmarked = useMemo(() => unmarkedKeys(review), [review]);
  // The rows the coach was stopped on, minus the ones decided since.
  const stillFlagged = useMemo(
    () => new Set(unmarked.filter((key) => flaggedKeys.has(key))),
    [unmarked, flaggedKeys],
  );

  if (loadError && !review) {
    return (
      <div>
        <ProjectHeader handleNavigation={() => history.push(backTo)} showBack />
        <div id="annual-okr-review">
          <div className="aokr-empty">{loadError}</div>
        </div>
      </div>
    );
  }

  const runState = review?.run_state;
  const readOnly = !REVIEWABLE_STATES.includes(runState);
  const canStart = REVIEW_SENT_STATES.includes(runState);
  const comments = review?.comments || [];

  const rowProps = (type, objective, extra = {}) => {
    const key = objectiveKey(type, objective.objective_id);
    return {
      type,
      objective,
      member:
        type === OBJECTIVE_TYPE.INDIVIDUAL
          ? (review.members || []).find(
              (m) => String(m.user_id) === String(objective.member?.user_id),
            )
          : null,
      comments,
      actions,
      readOnly,
      expanded: expandedKey === key,
      onToggle: () => setExpandedKey((open) => (open === key ? null : key)),
      flagged: stillFlagged.has(key),
      ...extra,
    };
  };

  // Highlight the rows still to decide and take the coach to the first.
  const flagUnmarked = (keys) => {
    setFlaggedKeys(new Set(keys));
    const [type, id] = keys[0].split(":");
    setTab(type === OBJECTIVE_TYPE.COMPANY ? TAB.COMPANY : TAB.INDIVIDUAL);
    setExpandedKey(keys[0]);
    requestAnimationFrame(() =>
      leftRef.current
        ?.querySelector(`[data-review-target="${type}-${id}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
  };

  // Always clickable: a blocked send points at the rows still to decide,
  // which a disabled button can't.
  const handleComplete = () => {
    if (unmarked.length) {
      flagUnmarked(unmarked);
      return;
    }
    setFlaggedKeys(new Set());
    setDialog("complete");
  };

  const submit = (body) =>
    quarterlyOkrStore
      .getState()
      .submitReview(qcId, body)
      .then(() => {
        setDialog(null);
        quarterlyOkrStore
          .getState()
          .fetchDashboard()
          .catch(() => null);
      })
      .catch(async (error) => {
        const { code, message } = readError(error);
        errorNoti(message);
        if (code === "unmarked_objectives") {
          setDialog(null);
          const fresh = await actions.refetch();
          const keys = unmarkedKeys(fresh);
          if (keys.length) flagUnmarked(keys);
        } else if (["already_submitted", "invalid_state"].includes(code)) {
          setDialog(null);
          actions.refetch();
        } else if (code === "review_not_in_progress") {
          setDialog(null);
          await quarterlyOkrStore.getState().enterReview(qcId);
          actions.refetch();
        }
      });

  const startLiveReview = () =>
    quarterlyOkrStore
      .getState()
      .startLiveReview(qcId)
      .then(() => {
        quarterlyOkrStore
          .getState()
          .fetchDashboard()
          .catch(() => null);
        history.push(
          QUARTERLY_OKR_LIVE_REVIEW.replace(":quarterCompanyId", qcId),
        );
      })
      .catch((error) => {
        errorNoti(readError(error).message);
        setDialog(null);
        actions.refetch();
      });

  const meetingWhen = formatMeetingDateTime(
    review?.meeting_datetime,
    "h:mm A [on] Do MMMM, YYYY",
  );
  const companyTarget = { scope: COMMENT_SCOPE.COMPANY };

  return (
    <div>
      <ProjectHeader handleNavigation={() => history.push(backTo)} showBack />
      <div id="annual-okr-review" className="qokr-coach-review">
        <div className="aokr-header">
          <div className="aokr-title">
            Quarterly OKRs{companyName ? ` — ${companyName}` : ""}
            {quarterLabel ? (
              <span className="qokr-coach-review__quarter">{quarterLabel}</span>
            ) : null}
          </div>
          <div className="aokr-header-right qokr-coach-review__header-right">
            {review?.counts ? (
              <div className="aokr-reviewed">
                Reviewed:
                <strong>
                  {" "}
                  {review.counts.marked} of {review.counts.total}
                </strong>
              </div>
            ) : null}
            {review ? (
              <QokrModeToggle
                options={MODE_OPTIONS}
                value={mode}
                onChange={setMode}
                ariaLabel="Review view"
              />
            ) : null}
          </div>
        </div>
        <div className="aokr-divider" />

        {!review ? (
          <AnnualOkrReviewSkeleton />
        ) : mode === MODE.ALIGNMENT ? (
          <div className="qokr-coach-review__align qokr-mode-pane">
            <QokrAlignmentView teamOkrs={alignmentFromReview(review)} />
          </div>
        ) : (
          <div className="scrm-container">
            <div className="scrm-left qokr-coach-review__left" ref={leftRef}>
              {review.send_mode === SEND_MODE.EARLY ? (
                <QokrBanner
                  tone="warn"
                  icon="fa-solid fa-forward"
                  title="The CEO sent this before everyone had submitted"
                >
                  {review.early_send_reason ||
                    "Unsubmitted drafts came to you as they were."}
                </QokrBanner>
              ) : null}

              <div className="qokr-tabs" role="tablist" aria-label="OKRs">
                {[
                  [
                    TAB.COMPANY,
                    "Company OKRs",
                    (review.company_objectives || []).length,
                  ],
                  [
                    TAB.INDIVIDUAL,
                    "Individual OKRs",
                    (review.individual_objectives || []).length,
                  ],
                ].map(([key, label, count]) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={tab === key}
                    className={`qokr-tab${tab === key ? " is-active" : ""}`}
                    onClick={() => setTab(key)}
                  >
                    {label}
                    <span className="qokr-tab__count">{count}</span>
                  </button>
                ))}
              </div>

              {tab === TAB.COMPANY ? (
                <ReviewCompanyOkrs review={review} rowProps={rowProps} />
              ) : (
                <ReviewIndividualOkrs
                  review={review}
                  rowProps={rowProps}
                  actions={actions}
                  readOnly={readOnly}
                />
              )}

              <div className="rm-overall">
                <div className="rm-overall-comments">
                  <ReviewCommentThread
                    comments={commentsFor(comments, companyTarget)}
                    readOnly={readOnly}
                    addLabel="Add your overall assessment for the CEO"
                    placeholder="Your overall read of this quarter's OKRs"
                    saving={Boolean(actions.pending[commentKey(companyTarget)])}
                    deletingId={deletingCommentId(actions.pending)}
                    onAdd={(text) => actions.addComment(companyTarget, text)}
                    onDelete={actions.deleteComment}
                  />
                </div>
              </div>

              {(review.deferred_annual_okrs || []).length ||
              (review.completed_annual_okrs || []).length ? (
                <section className="qokr-coach-review__annual">
                  <h3 className="qokr-coach-review__annual-title">
                    Annual OKRs not in this quarter
                  </h3>
                  <ul>
                    {(review.deferred_annual_okrs || []).map((a) => (
                      <li key={`d-${a.annual_objective_id}`}>
                        <span className="qokr-pill qokr-pill--grey">
                          Deferred
                        </span>
                        {a.statement}
                      </li>
                    ))}
                    {(review.completed_annual_okrs || []).map((a) => (
                      <li key={`c-${a.annual_objective_id}`}>
                        <span className="qokr-pill qokr-pill--green">
                          Completed
                        </span>
                        {a.statement}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>

            <div className="scrm-right">
              <div className="rm-summary-header">
                <span className="rm-summary-header-title">Review Summary</span>
              </div>
              <div className="rm-summary-header-divider" />

              {summaryGroups.length ? (
                <div className="rm-summary-filters">
                  <FilterChips
                    value={summaryFilter}
                    onChange={setSummaryFilter}
                    options={[
                      {
                        key: "all",
                        label: "All",
                        count: countWhere(() => true),
                      },
                      {
                        key: "discuss",
                        label: "To discuss",
                        count: countWhere(
                          (item) => item.status === REVIEW_STATUS.TO_DISCUSS,
                        ),
                      },
                      {
                        key: "comments",
                        label: "With comments",
                        count: countWhere((item) => item.comments.length > 0),
                      },
                    ]}
                  />
                </div>
              ) : null}

              <div className="rm-right-body">
                {summaryGroups.length ? (
                  <SummaryGroups
                    groups={visibleGroups}
                    emptyLabel="Nothing matches this filter."
                  />
                ) : (
                  <div className="rm-summary-empty">
                    Your review will appear here.
                  </div>
                )}
              </div>

              <div className="rm-right-footer">
                {readOnly ? (
                  <>
                    {canStart ? (
                      <button
                        type="button"
                        className="rm-complete-btn"
                        onClick={() => setDialog("start")}
                      >
                        Start Live Review
                        <ArrowRightIcon />
                      </button>
                    ) : null}
                    <div
                      className={`rm-meeting-scheduled${
                        meetingWhen && isMeetingPast(review.meeting_datetime)
                          ? " rm-meeting-scheduled--missed"
                          : ""
                      }`}
                    >
                      <span>This review has been sent to the CEO.</span>
                      {meetingWhen &&
                      runState === RUN_STATE.LIVE_REVIEW_SCHEDULED ? (
                        <strong>
                          Live review scheduled for {meetingWhen}.
                        </strong>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="rm-complete-btn"
                      onClick={handleComplete}
                    >
                      Complete review &amp; send to CEO
                      <ArrowRightIcon />
                    </button>
                    {stillFlagged.size ? (
                      <p className="rm-pending-note" role="alert">
                        {stillFlagged.size === 1
                          ? "1 objective still needs a decision — accept it, or flag it to discuss."
                          : `${stillFlagged.size} objectives still need a decision — accept each one, or flag it to discuss.`}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {dialog === "complete" && review ? (
        <ReviewCompleteModal
          review={review}
          comments={comments}
          submitting={Boolean(actions.pending.submit)}
          onClose={() => setDialog(null)}
          onSubmit={submit}
        />
      ) : null}

      {dialog === "start" ? (
        <StartLiveReviewDialog
          starting={Boolean(actions.pending.startLive)}
          onStart={startLiveReview}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
};

export default QuarterlyOkrReview;
